import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type React from 'react';
import { createContext, useState, useContext } from 'react';
import type { Editor } from '@tiptap/react';
import { UndoRedoManagerCommandVersion } from '../../UndoRedo/UndoRedoManagerCommandVersion.ts';

export interface ActiveEditorContextProps {
  activeEditor: Editor | null;
  setActiveEditor: (editor: Editor | null) => void;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

interface ActiveEditorProviderProps {
  children: ReactNode;
}

export const ActiveEditorContext = createContext<ActiveEditorContextProps>({} as ActiveEditorContextProps);

export const ActiveEditorProvider: React.FC<ActiveEditorProviderProps> = ({ children }) => {
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
  const previousEditorRef = useRef<Editor | null>(null);
  const isFirstActiveEditorRunRef = useRef(true);

  // Runs after commit (not during ActiveEditorProvider's render) so that clearing the
  // previous editor's selection can't synchronously force-update the component that owns it.
  useEffect(() => {
    // Skip the initial mount: there is no real handoff yet, and the `isEditMode` effect
    // below already reports `hasTiptapFocus: false` once on mount — running this effect's
    // body too would report it a second time.
    if (isFirstActiveEditorRunRef.current) {
      isFirstActiveEditorRunRef.current = false;
      previousEditorRef.current = activeEditor;
      return;
    }

    const previousEditor = previousEditorRef.current;
    if (previousEditor && previousEditor !== activeEditor) {
      previousEditor.commands.clearSelection();
      previousEditor.commands.setTextSelection(0);
    }
    previousEditorRef.current = activeEditor;

    if (activeEditor) {
      const blockId = activeEditor.storage.blockId;
      if (blockId) {
        undoRedoManager.updateFocusContext({
          focusedBlockId: blockId,
          focusedBlockType: 'text',
          hasTiptapFocus: true,
        });
      }
    } else {
      undoRedoManager.updateFocusContext({
        hasTiptapFocus: false,
      });
    }
  }, [activeEditor, undoRedoManager]);

  useEffect(() => {
    if (!isEditMode) {
      undoRedoManager.updateFocusContext({
        hasTiptapFocus: false,
      });
    }
  }, [isEditMode, undoRedoManager]);

  const context: ActiveEditorContextProps = { activeEditor, setActiveEditor, isEditMode, setIsEditMode };

  return <ActiveEditorContext.Provider value={context}>{children}</ActiveEditorContext.Provider>;
};

export const useActiveEditor = (): ActiveEditorContextProps => {
  const context = useContext(ActiveEditorContext);

  if (!context) {
    throw new Error('useActiveEditor must be used within an ActiveEditorProvider');
  }

  return context;
};
